<template>
  <div class="register">
    <div v-if="!registered">
      <p class="register-step">{{ $t('register.stepOf', { step: step, total: 2 }) }}</p>

      <!-- Step 1: account details. Nothing is sent to the API until step 2. -->
      <b-form v-if="step === 1" novalidate @submit.prevent="toStep2">
        <h2 ref="title1" tabindex="-1">{{ $t('register.accountTitle') }}</h2>
        <p class="register-lead">{{ $t('register.accountLead') }}</p>

        <b-alert v-for="(error, index) in errors" :key="'e' + index" show variant="danger">{{ error }}</b-alert>

        <b-form-group
          :label="$t('register.registerName')"
          label-for="reg-username"
          :description="$t('register.usernameHint')"
          :invalid-feedback="usernameErr ? $t('register.err.' + usernameErr) : ''"
          :state="usernameState"
        >
          <b-form-input
            id="reg-username"
            ref="username"
            v-model="form.username"
            type="text"
            autocomplete="username"
            autocapitalize="off"
            spellcheck="false"
            maxlength="64"
            :state="usernameState"
            @blur="touched.username = true"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          :label="$t('register.email')"
          label-for="reg-email"
          :description="$t('register.emailHint')"
          :invalid-feedback="emailErr ? $t('register.err.' + emailErr) : ''"
          :state="emailState"
        >
          <b-form-input
            id="reg-email"
            ref="email"
            v-model="form.email"
            type="email"
            inputmode="email"
            autocomplete="email"
            autocapitalize="off"
            spellcheck="false"
            maxlength="254"
            :state="emailState"
            @blur="touched.email = true"
          ></b-form-input>
        </b-form-group>

        <b-form-group :label="$t('register.password')" label-for="reg-password">
          <div class="register-password">
            <b-form-input
              id="reg-password"
              ref="password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              :passwordrules="generatorRules"
              maxlength="64"
              aria-describedby="reg-password-rules"
              :state="passwordState"
              @blur="onPasswordBlur"
            ></b-form-input>
            <button
              type="button"
              class="register-eye"
              :aria-label="eyeLabel"
              :title="eyeLabel"
              :aria-pressed="String(showPassword)"
              @click="showPassword = !showPassword"
            >
              <b-icon :icon="showPassword ? 'eye-slash' : 'eye'" aria-hidden="true"></b-icon>
            </button>
          </div>
          <ul id="reg-password-rules" class="register-rules">
            <li v-for="item in checklist" :key="item.key" :class="'is-' + item.state" :role="item.live ? 'status' : null">
              <b-icon :icon="ruleIcon(item.state)" :animation="item.state === 'checking' ? 'spin' : null" aria-hidden="true"></b-icon>
              <span>{{ $t('register.rule.' + item.key) }}</span>
            </li>
          </ul>
        </b-form-group>

        <!-- Honeypot: off-screen and skipped by keyboard and screen readers, so
             only bots fill it in. -->
        <div class="register-hp" aria-hidden="true">
          <label for="reg-website">Website</label>
          <input id="reg-website" v-model="honeypot" type="text" name="website" tabindex="-1" autocomplete="off" />
        </div>

        <b-button type="submit" variant="primary" block :disabled="busy">
          {{ busy ? $t('register.checking') : $t('register.continue') }}
        </b-button>
        <p class="register-switch">
          {{ $t('register.haveAccount') }}
          <a href="#" @click.prevent="toLogin">{{ $t('account.login') }}</a>
        </p>
      </b-form>

      <!-- Step 2: an optional card, which activates the account straight away. -->
      <div v-else>
        <h2 ref="title2" tabindex="-1">{{ $t('register.activateTitle') }}</h2>
        <p class="register-lead">{{ $t('register.activateLead') }}</p>

        <b-alert v-for="(error, index) in errors" :key="'e' + index" show variant="danger">{{ error }}</b-alert>

        <stripe-credit-card
          class="register-card"
          :submit-text="busy ? $t('register.creating') : $t('register.verifyAndCreate')"
          submit-variant="primary"
          @tokenCreated="createWithCard"
        ></stripe-credit-card>
        <a class="register-stripe" target="_blank" rel="noopener noreferrer" href="https://stripe.com">
          <img src="/static/powered_by_stripe.png" alt="Powered by Stripe" />
        </a>

        <div class="register-skip">
          <p>{{ $t('register.skipHint') }}</p>
          <b-button variant="warning" block :disabled="busy" @click="createWithoutCard">{{ $t('register.skip') }}</b-button>
        </div>
        <p class="register-switch">
          <a href="#" @click.prevent="backToStep1">← {{ $t('buttons.back') }}</a>
        </p>
      </div>
    </div>

    <div v-else>
      <h2>{{ user.username }} {{ $t('register.registerSuc') }}</h2>
      <p v-if="withCard">{{ $t('register.registerEmail') }} {{ user.email }}</p>
      <p v-else>{{ $t('register.registerActi') }}</p>
    </div>
  </div>
</template>

<script>
import StripeCreditCard from '@/components/StripeCreditCard.vue'
import { cleanText, usernameError, emailError, passwordRules, pwnedCount, PASSWORD_GENERATOR_RULES } from '../registerPolicy'

// Bots tend to submit within moments of the form appearing.
const MIN_FILL_MS = 2500

// Checklist line and icon state for each leaked-password check state.
const LEAK_ITEMS = {
  idle: { key: 'leakIdle', state: 'idle' },
  checking: { key: 'leakChecking', state: 'checking' },
  ok: { key: 'leakOk', state: 'ok' },
  found: { key: 'leakFound', state: 'bad' },
  unknown: { key: 'leakUnknown', state: 'warn' },
}

export default {
  components: {
    StripeCreditCard,
  },
  data () {
    return {
      step: 1,
      form: {
        username: '',
        email: '',
        password: '',
      },
      honeypot: '',
      showPassword: false,
      // Field errors show once a field has been left, or after Continue.
      touched: { username: false, email: false, password: false },
      submitted: false,
      // Leaked-password check for the password it was run on:
      // idle | checking | ok | found | unknown (service unreachable).
      leak: { state: 'idle', for: '' },
      busy: false,
      errors: [],
      registered: false,
      withCard: false,
      user: {},
      shownAt: Date.now(),
      // Lets the browser's password generator suggest something that passes.
      generatorRules: PASSWORD_GENERATOR_RULES,
    }
  },
  computed: {
    username () {
      return cleanText(this.form.username)
    },
    email () {
      return cleanText(this.form.email)
    },
    usernameErr () {
      return usernameError(this.username)
    },
    emailErr () {
      return emailError(this.email)
    },
    usernameState () {
      return this.fieldState('username', this.usernameErr)
    },
    emailState () {
      return this.fieldState('email', this.emailErr)
    },
    localRules () {
      return passwordRules(this.form.password, { username: this.username, email: this.email })
    },
    localRulesOk () {
      return this.localRules.every((r) => r.ok)
    },
    leakState () {
      return this.leak.for === this.form.password ? this.leak.state : 'idle'
    },
    passwordState () {
      if (!this.form.password) return null
      return this.fieldState('password', !this.localRulesOk || this.leakState === 'found')
    },
    // The checklist under the password: grey until there is something to
    // judge, then green or red. The leak check runs on leaving the field and
    // on Continue, not on every keystroke.
    checklist () {
      const started = this.form.password.length > 0
      const items = this.localRules.map((r) => ({
        key: r.key,
        state: !started ? 'idle' : r.ok ? 'ok' : 'bad',
      }))
      items.push({ ...LEAK_ITEMS[this.leakState], live: true })
      return items
    },
    eyeLabel () {
      return this.$t(this.showPassword ? 'register.hidePassword' : 'register.showPassword')
    },
  },
  methods: {
    fieldState (field, error) {
      if (!(this.touched[field] || this.submitted)) return null
      return error ? false : null
    },
    ruleIcon (state) {
      return {
        ok: 'check-circle-fill',
        bad: 'x-circle-fill',
        warn: 'dash-circle',
        checking: 'arrow-repeat',
      }[state] || 'circle'
    },
    onPasswordBlur () {
      this.touched.password = true
      this.checkLeaks()
    },
    // Runs the leaked-password check once per password value and returns its
    // promise, so Continue also waits for a check that leaving the field has
    // already started. A result that arrives after the password has changed is
    // discarded.
    checkLeaks () {
      const password = this.form.password
      if (!this.localRulesOk) return Promise.resolve()
      if (this.leak.for === password && this.leak.state !== 'idle') return this.leakCheck || Promise.resolve()
      this.leak = { state: 'checking', for: password }
      this.leakCheck = pwnedCount(password).then((count) => {
        if (this.leak.for !== password) return
        this.leak = { state: count === null ? 'unknown' : count > 0 ? 'found' : 'ok', for: password }
      })
      return this.leakCheck
    },
    async toStep2 () {
      this.submitted = true
      this.errors = []
      if (this.usernameErr) return this.focus('username')
      if (this.emailErr) return this.focus('email')
      if (!this.localRulesOk) {
        this.errors.push(this.$t('register.err.password'))
        return this.focus('password')
      }
      this.busy = true
      await this.checkLeaks()
      this.busy = false
      if (this.leakState === 'found') {
        this.errors.push(this.$t('register.err.leaked'))
        return this.focus('password')
      }
      this.step = 2
      this.focus('title2')
    },
    backToStep1 () {
      this.errors = []
      this.step = 1
      this.focus('title1')
    },
    focus (ref) {
      this.$nextTick(() => {
        const el = this.$refs[ref]
        if (el && el.focus) el.focus()
      })
    },
    createWithCard (token) {
      if (this.busy || !token || !token.id) return
      this.submit(token.id)
    },
    createWithoutCard () {
      if (this.busy) return
      this.submit(null)
    },
    // Step 2 is only reachable once step 1 has passed every check, and nothing
    // in step 1 can change while step 2 is shown.
    submit (stripeToken) {
      this.errors = []
      if (this.honeypot !== '' || Date.now() - this.shownAt < MIN_FILL_MS) {
        this.errors.push(this.$t('register.err.bot'))
        this.shownAt = Date.now()
        return
      }
      const credentials = {
        username: this.username,
        email: this.email,
        password: this.form.password,
      }
      if (stripeToken) credentials.stripeToken = stripeToken
      this.busy = true
      this.withCard = !!stripeToken
      this.$auth.register(this, credentials).then(
        () => {
          // The password is not needed once the account exists.
          if (this.registered) this.form.password = ''
        },
        () => {}
      ).finally(() => {
        this.busy = false
      })
    },
    toLogin () {
      this.$bvModal.hide('register')
      this.$bvModal.show('login')
    },
  },
}
</script>

<style lang="scss" scoped>
// Pages such as ScheduleCampaign.vue leak an unscoped
// `.form-group { margin-bottom: 0 }`; keep the fields apart in the app. The
// landing page sets its own spacing in _auth-modal.scss.
.register ::v-deep .form-group {
  margin-bottom: 16px;
}

.register-step {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
}
h2:focus {
  outline: none;
}
.register-lead {
  margin-bottom: 18px;
}

// Show/hide button inside the password box.
.register-password {
  position: relative;

  ::v-deep .form-control {
    padding-right: 48px;
  }
}
.register-eye {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;

  &:hover,
  &:focus {
    opacity: 1;
  }
}

// Password checklist.
.register-rules {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: grid;
  gap: 5px;
  font-size: 13px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #6c757d;

    .b-icon {
      flex: none;
      margin-top: 2px;
    }
  }
  .is-ok {
    color: #1e7e34;
  }
  .is-bad {
    color: #c82333;
  }
}

.register-hp {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.register-switch {
  margin: 16px 0 0;
  text-align: center;
  font-size: 14px;

  // IPAM/Header.vue ships an unscoped `a { display: block }` that reaches
  // every page, which would put the link on a line of its own.
  a {
    display: inline;
  }
}

// The card form's own button is the step's main action, full width like
// Continue in step 1.
.register-card ::v-deep .btn {
  width: 100%;
}

.register-stripe {
  display: block;
  margin-top: 10px;
  text-align: right;

  img {
    height: 26px;
  }
}

.register-skip {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  p {
    font-size: 13.5px;
    margin-bottom: 10px;
  }
}
</style>
